import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchProducts, Product, ProductQueryParams } from "../services/productService";
import {
  ShoppingBagIcon,
  TruckIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CurrencyDollarIcon,
  BoltIcon,
  MapPinIcon,
  FireIcon,
  CalculatorIcon,
  CheckBadgeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { UnifiedCTA } from "./UnifiedCTA";

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return null;
};

const clearCookie = (name: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const clearAuthArtifacts = () => {
  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("currentTenant");
    clearCookie("auth_token");
    clearCookie("user_data");
  } catch {
    // ignore local storage failures
  }
};

const getCurrentUserData = () => {
  const raw = localStorage.getItem("user_data") || getCookieValue("user_data");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = localStorage.getItem("currentTenant");
  const user = localStorage.getItem("user");
  const userData = localStorage.getItem("user_data");
  const cookieUserData = getCookieValue("user_data");
  if (tenant) return JSON.parse(tenant).id;
  if (user) return JSON.parse(user).tenant_id;
  if (userData) return JSON.parse(userData).tenant_id;
  if (cookieUserData) return JSON.parse(cookieUserData).tenant_id;
  return "";
};

const hasValidTenantContext = () => {
  const tenantId = getTenantId();
  const userData = getCurrentUserData();
  const authToken =
    localStorage.getItem("auth_token") || getCookieValue("auth_token");
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    tenantId
  );
  return Boolean(authToken && userData?.tenant_id && isUuid);
};

const useLandingProducts = (params: ProductQueryParams) => {
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!hasValidTenantContext()) {
      setProducts([]);
      return;
    }
    const tenantId = getTenantId();
    fetchProducts(tenantId, { limit: params.limit, is_available: true, ...params })
      .then(setProducts)
      .catch(() => {
        clearAuthArtifacts();
        setProducts([]);
      });
  }, [params.limit, params.is_available, params.featured, params.sort_by, params.sort_order, params.search]);

  return products;
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount);

// Hero Section
const Hero = () => (
  <section className="agri-hero-diagonal agri-grain-overlay relative overflow-hidden pt-24 pb-20">
    <div
      className="pointer-events-none absolute inset-0 bg-center bg-no-repeat bg-contain opacity-10"
      style={{ backgroundImage: "url('/favicon.png')" }}
      aria-hidden="true"
    />
    <div className="relative container mx-auto px-4 flex flex-col lg:flex-row items-center gap-12">
      <div className="flex-1">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-agri-forest-800 mb-4">
          AgriFlow Food Hub
        </h1>
        <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-xl">
          Nigeria’s trusted digital marketplace for food producers and buyers. Discover, order, and grow your business with AgriFlow Food Hub.
        </p>
        <div className="flex gap-4">
          <Link
            to="/products"
            className="btn-primary text-white px-6 py-3 rounded-lg font-semibold shadow"
          >
            Browse Products
          </Link>
          <Link
            to="/register"
            className="btn-secondary px-6 py-3 rounded-lg font-semibold"
          >
            Partner With Us
          </Link>
        </div>
      </div>
      <div className="flex-1 flex justify-center">
        <div className="agri-organic-card p-4 w-full max-w-xl">
          <img
            src="/agriflow_icon.png"
            alt="AgriFlow Food Hub"
            className="w-full object-contain"
          />
        </div>
      </div>
    </div>
  </section>
);

// Live Product Availability Feed
const LiveAvailability = () => {
  const fetched = useLandingProducts({ limit: 8, sort_by: "current_stock", sort_order: "desc" });
  const fallback = [
    { name: "Fresh Tomatoes", quantity: "500kg", location: "Lagos", status: "high" },
    { name: "Ofada Rice", quantity: "2 tons", location: "Ogun", status: "high" },
    { name: "Palm Oil (Red)", quantity: "300L", location: "Delta", status: "medium" },
    { name: "Yam Tubers", quantity: "1 ton", location: "Benue", status: "high" },
    { name: "Brown Beans", quantity: "150kg", location: "Plateau", status: "low" },
    { name: "Fresh Pepper", quantity: "200kg", location: "Lagos", status: "medium" },
  ];
  const [products, setProducts] = useState(fallback);

  useEffect(() => {
    if (fetched.length === 0) return;
    const mapped = fetched.map((product) => ({
      name: product.name,
      quantity: `${product.current_stock} ${product.unit}`,
      location: "Multiple locations",
      status: product.current_stock > 100 ? "high" : product.current_stock > 20 ? "medium" : "low",
    }));
    setProducts(mapped);
  }, [fetched]);

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      setProducts((prev) => [...prev.slice(1), prev[0]]);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-4 bg-green-700 text-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 flex-shrink-0">
            <BoltIcon className="w-5 h-5 animate-pulse" />
            <span className="font-semibold text-sm">LIVE INVENTORY:</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="flex animate-scroll space-x-8">
              {products.concat(products).map((product, i) => (
                <div key={i} className="flex items-center space-x-2 whitespace-nowrap">
                  <span className={`w-2 h-2 rounded-full ${product.status === 'high' ? 'bg-green-300' :
                    product.status === 'medium' ? 'bg-yellow-300' : 'bg-red-300'
                    } animate-pulse`} />
                  <span className="text-sm">
                    <strong>{product.name}</strong> - {product.quantity} in {product.location}
                  </span>
                  {product.status === 'low' && (
                    <span className="text-xs bg-red-500 px-2 py-0.5 rounded">Low Stock!</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

// Today's Best Deals
const TodayDeals = () => {
  const featured = useLandingProducts({ limit: 3, featured: true, sort_by: "created_at", sort_order: "desc" });
  const featuredFallback = useLandingProducts({ limit: 3, sort_by: "created_at", sort_order: "desc" });
  const colors = [
    "from-red-500 to-orange-500",
    "from-green-500 to-emerald-500",
    "from-blue-500 to-indigo-500",
  ];
  const source = featured.length > 0 ? featured : featuredFallback;
  const deals = source.map((product, i) => ({
    product: product.name,
    discount: formatCurrency(product.price_per_unit),
    condition: `Available: ${product.current_stock} ${product.unit}`,
    expires: product.is_available ? "Available now" : "Out of stock",
    color: colors[i % colors.length],
    id: product.id,
  }));

  return (
    <section className="py-12 bg-gradient-to-r from-orange-50 to-red-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center mb-8">
          <FireIcon className="w-8 h-8 text-red-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-900">Today's Hot Deals</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {deals.length === 0 && (
            <div className="col-span-3 text-center text-gray-500 text-sm">
              No featured products yet. Add products to show deals here.
            </div>
          )}
          {deals.map((deal, i) => (
            <div
              key={i}
              className={`relative bg-gradient-to-br ${deal.color} text-white rounded-xl p-6 shadow-lg overflow-hidden`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <div className="text-3xl font-extrabold mb-2">{deal.discount}</div>
                <div className="text-xl font-semibold mb-2">{deal.product}</div>
                <div className="text-sm opacity-90 mb-3">{deal.condition}</div>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    <ClockIcon className="w-3 h-3 inline mr-1" />
                    {deal.expires}
                  </span>
                  {deal.id && (
                    <Link
                      to={`/products/${deal.id}`}
                      className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition"
                    >
                      View Product
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works
const HowItWorks = () => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
        How AgriFlow Works
      </h2>
      <div className="grid md:grid-cols-4 gap-8">
        {[
          {
            icon: UserGroupIcon,
            title: "Sign Up",
            desc: "Create your free AgriFlow account as a buyer, retailer, or producer.",
          },
          {
            icon: ShoppingBagIcon,
            title: "Browse & Order",
            desc: "Explore real-time inventory, compare prices, and place orders easily.",
          },
          {
            icon: TruckIcon,
            title: "We Deliver",
            desc: "AgriFlow handles logistics for fast, reliable delivery nationwide.",
          },
          {
            icon: ChartBarIcon,
            title: "Grow Together",
            desc: "Track your business, access analytics, and unlock new opportunities.",
          },
        ].map((step, i) => (
          <div
            key={i}
            className="flex flex-col items-center text-center p-6 bg-green-50 rounded-lg shadow-sm"
          >
            <step.icon className="w-10 h-10 text-green-600 mb-4" />
            <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
            <p className="text-gray-600">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Why Choose AgriFlow (Merged: Features + QualityGuarantee)
const WhyChooseAgriFlow = () => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
        Why Choose AgriFlow?
      </h2>
      <div className="grid md:grid-cols-4 gap-6">
        {[
          {
            icon: ShoppingBagIcon,
            title: "Extensive Food Inventory",
            desc: "Thousands of Nigerian food products – rice, beans, palm oil, yam, spices, and more.",
          },
          {
            icon: CurrencyDollarIcon,
            title: "Competitive Wholesale Pricing",
            desc: "Best rates, volume discounts, and special pricing for regular customers.",
          },
          {
            icon: TruckIcon,
            title: "Reliable Supply Chain",
            desc: "Fast, reliable delivery across Nigeria. Logistics handled for you.",
          },
          {
            icon: BuildingOfficeIcon,
            title: "Business Partnership",
            desc: "Retailers, restaurants, and distributors welcome. Credit terms for established partners.",
          },
          {
            icon: DevicePhoneMobileIcon,
            title: "Easy Online Ordering",
            desc: "Order 24/7 from your phone or computer. Simple, digital, and transparent.",
          },
          {
            icon: CheckBadgeIcon,
            title: "NAFDAC Certified",
            desc: "All products meet Nigerian food safety standards with full compliance.",
          },
          {
            icon: ShieldCheckIcon,
            title: "Quality Inspection",
            desc: "Every batch is inspected before delivery. Freshness and authenticity guaranteed.",
          },
          {
            icon: ClockIcon,
            title: "Freshness Guaranteed",
            desc: "Fresh products delivered within 48 hours of harvest. Farm-to-table traceability.",
          },
        ].map((feature, i) => (
          <div
            key={i}
            className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition"
          >
            <div className="bg-green-100 rounded-full p-3 mb-4">
              <feature.icon className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
            <p className="text-gray-600 text-sm">{feature.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Who We Serve
const WhoWeServe = () => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
        Who We Serve
      </h2>
      <div className="grid md:grid-cols-4 gap-8">
        {[
          {
            icon: BuildingOfficeIcon,
            title: "Retailers",
            desc: "Access wholesale food products and reliable delivery for your store.",
          },
          {
            icon: UserGroupIcon,
            title: "Restaurants",
            desc: "Source fresh, authentic Nigerian ingredients for your kitchen.",
          },
          {
            icon: TruckIcon,
            title: "Distributors",
            desc: "Expand your product range and reach new markets with AgriFlow.",
          },
          {
            icon: ShoppingBagIcon,
            title: "Farmers & Cooperatives",
            desc: "Sell directly to buyers, get fair prices, and grow your business.",
          },
        ].map((aud, i) => (
          <div
            key={i}
            className="flex flex-col items-center text-center p-6 bg-green-50 rounded-lg shadow-sm"
          >
            <aud.icon className="w-10 h-10 text-green-600 mb-4" />
            <h3 className="font-semibold text-lg mb-2">{aud.title}</h3>
            <p className="text-gray-600">{aud.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Coverage & Impact (Merged: DeliveryMap + ImpactStats)
const CoverageAndImpact = () => {
  const regions = [
    { name: "Lagos", delivery: "Same Day", color: "bg-green-600" },
    { name: "Ogun", delivery: "Same Day", color: "bg-green-600" },
    { name: "Abuja", delivery: "Next Day", color: "bg-blue-600" },
    { name: "Port Harcourt", delivery: "Next Day", color: "bg-blue-600" },
    { name: "Enugu", delivery: "Next Day", color: "bg-blue-600" },
    { name: "Ibadan", delivery: "Next Day", color: "bg-blue-600" },
    { name: "Kano", delivery: "2-3 Days", color: "bg-yellow-600" },
    { name: "Kaduna", delivery: "2-3 Days", color: "bg-yellow-600" },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center mb-4">
          <MapPinIcon className="w-8 h-8 text-green-600 mr-2" />
          <h2 className="text-2xl font-bold text-green-700">
            Nationwide Coverage & Impact
          </h2>
        </div>
        <p className="text-center text-gray-600 mb-8">
          Fast, reliable delivery across 18 Nigerian states
        </p>

        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {regions.map((region, i) => (
            <div
              key={i}
              className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-600"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900">{region.name}</div>
                  <div className="text-sm text-gray-600">{region.delivery}</div>
                </div>
                <span className={`w-3 h-3 rounded-full ${region.color}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Delivery & Business Stats */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8">
          <div className="grid md:grid-cols-4 lg:grid-cols-7 gap-6 text-center">
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">18+</div>
              <div className="text-gray-600 text-sm">States Covered</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">24-48hrs</div>
              <div className="text-gray-600 text-sm">Avg Delivery</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">98%</div>
              <div className="text-gray-600 text-sm">On-Time Rate</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">5,000+</div>
              <div className="text-gray-600 text-sm">Products</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">850+</div>
              <div className="text-gray-600 text-sm">Customers</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">12,000+</div>
              <div className="text-gray-600 text-sm">Orders</div>
            </div>
            <div className="lg:col-span-1">
              <div className="text-3xl font-bold text-green-700 mb-2">₦75M+</div>
              <div className="text-gray-600 text-sm">Monthly Revenue</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Popular This Week
const PopularProducts = () => {
  const fetched = useLandingProducts({ limit: 8, featured: true, sort_by: "current_stock", sort_order: "desc" });
  const fetchedFallback = useLandingProducts({ limit: 8, sort_by: "current_stock", sort_order: "desc" });
  const fallback = [
    {
      id: "fallback-1",
      name: "Premium Ofada Rice",
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      price: "₦35,000",
      unit: "per 50kg bag",
      badge: "Bestseller",
      supplier: "Local supplier",
    },
    {
      id: "fallback-2",
      name: "Extra Virgin Palm Oil",
      image: "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      price: "₦1,500",
      unit: "per liter",
      badge: "Top Rated",
      supplier: "Local supplier",
    },
    {
      id: "fallback-3",
      name: "Fresh Yam Tubers",
      image: "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=400&q=80",
      price: "₦5,000",
      unit: "per tuber",
      badge: "Trending",
      supplier: "Local supplier",
    },
    {
      id: "fallback-4",
      name: "Brown Beans (Oloyin)",
      image: "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      price: "₦28,000",
      unit: "per 50kg",
      badge: "Hot Deal",
      supplier: "Local supplier",
    },
  ];

  const productsSource = fetched.length > 0 ? fetched : fetchedFallback;
  const products =
    productsSource.length > 0
      ? productsSource.map((product, i) => ({
        id: product.id,
        name: product.name,
        image: product.product_images?.[0] || fallback[i % fallback.length].image,
        price: formatCurrency(product.price_per_unit),
        unit: `per ${product.unit}`,
        badge: product.current_stock > 100 ? "In Stock" : product.current_stock > 0 ? "Low Stock" : "Out",
        supplier: product.seller_name ? `Supplier: ${product.seller_name}` : "Supplier: N/A",
      }))
      : fallback;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
          🔥 Popular This Week
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {products.map((product, i) => (
            <div
              key={product.id || i}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition group"
            >
              <div className="relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover group-hover:scale-110 transition duration-300"
                />
                <span className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  {product.badge}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {product.name}
                </h3>
                <div className="text-xs text-gray-500 mb-2">{product.supplier}</div>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-green-700">
                    {product.price}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">
                    {product.unit}
                  </span>
                </div>
                {product.id && product.id.startsWith("fallback") ? (
                  <button className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition">
                    Quick Order
                  </button>
                ) : (
                  <Link
                    to={`/products/${product.id}`}
                    className="block text-center w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition"
                  >
                    View Product
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Bulk Order Calculator
const BulkCalculator = () => {
  const [quantity, setQuantity] = useState(50);
  const basePrice = 700;

  const calculateDiscount = (qty: number) => {
    if (qty >= 500) return 20;
    if (qty >= 200) return 15;
    if (qty >= 100) return 10;
    if (qty >= 50) return 5;
    return 0;
  };

  const discount = calculateDiscount(quantity);
  const subtotal = quantity * basePrice;
  const savings = subtotal * (discount / 100);
  const total = subtotal - savings;

  return (
    <section className="py-16 bg-gradient-to-br from-green-600 to-emerald-700 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <CalculatorIcon className="w-10 h-10 mr-3" />
            <h2 className="text-3xl font-bold">Bulk Order Savings Calculator</h2>
          </div>
          <p className="text-center text-green-100 mb-8">
            See how much you save when you order in bulk! Example: Rice @ ₦700/kg
          </p>

          <div className="bg-white text-gray-900 rounded-2xl p-8 shadow-2xl">
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">
                Order Quantity (kg)
              </label>
              <input
                type="range"
                min="10"
                max="1000"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full h-3 bg-green-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-2">
                <span className="text-sm text-gray-600">10 kg</span>
                <span className="text-2xl font-bold text-green-700">
                  {quantity} kg
                </span>
                <span className="text-sm text-gray-600">1,000 kg</span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between py-2 border-b">
                <span>Base Price:</span>
                <span className="font-semibold">₦{basePrice.toLocaleString()}/kg</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span>Subtotal:</span>
                <span className="font-semibold">₦{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b text-green-600">
                <span>Bulk Discount ({discount}%):</span>
                <span className="font-semibold">-₦{savings.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-3 text-xl">
                <span className="font-bold">Total:</span>
                <span className="font-bold text-green-700">
                  ₦{total.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 mb-4">
              <div className="text-sm font-semibold text-green-800 mb-2">
                💰 Discount Tiers:
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                <div>50-99 kg: <strong>5% off</strong></div>
                <div>100-199 kg: <strong>10% off</strong></div>
                <div>200-499 kg: <strong>15% off</strong></div>
                <div>500+ kg: <strong>20% off</strong></div>
              </div>
            </div>

            <button className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg">
              Request Quote for {quantity}kg
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};





// Testimonials
const Testimonials = () => (
  <section className="py-16 bg-white" id="testimonials">
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
        What Our Users Say
      </h2>
      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            name: "Adaeze Okafor",
            role: "Food Retailer",
            company: "Okafor Foodstuffs, Enugu",
            content:
              "AgriFlow connected me directly with rice farmers in Ebonyi. My customers love the quality and my profits increased by 35%.",
            rating: 5,
          },
          {
            name: "Emeka Ugwu",
            role: "Palm Oil Producer",
            company: "Ugwu Farms, Abakaliki",
            content:
              "The platform helped me reach retailers across the South-East. I can now sell my palm oil at better prices with guaranteed payment.",
            rating: 5,
          },
          {
            name: "Chioma Nwankwo",
            role: "Cooperative Leader",
            company: "Ebonyi Women Farmers Association",
            content:
              "Our 150+ women farmers now have direct market access. We're selling yam, beans, and vegetables at fair prices.",
            rating: 5,
          },
        ].map((t, i) => (
          <div
            key={i}
            className="bg-green-50 p-6 rounded-lg shadow-sm flex flex-col items-center"
          >
            <div className="flex items-center mb-2">
              {[...Array(t.rating)].map((_, j) => (
                <span key={j} className="text-green-500 text-xl">
                  ★
                </span>
              ))}
            </div>
            <p className="text-gray-700 italic mb-4">“{t.content}”</p>
            <div className="font-semibold text-green-700">{t.name}</div>
            <div className="text-sm text-gray-500">
              {t.role}, {t.company}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Food Items Showcase
const foodRows = [
  [
    {
      image:
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      title: "Ofada Rice",
      desc: "Premium local rice, unpolished and aromatic.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=400&q=80",
      title: "Yam Tubers",
      desc: "Fresh white yam, perfect for pounded yam or porridge.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Palm Oil",
      desc: "Rich, red palm oil sourced from local farmers.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "Brown Beans",
      desc: "Nutritious brown beans, ideal for moi-moi and akara.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Egusi Seeds",
      desc: "Melon seeds for classic Nigerian soups.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Maize",
      desc: "Fresh and dried maize for pap, tuwo, and more.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      title: "Cassava",
      desc: "High-quality cassava for garri and fufu.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      title: "Fresh Pepper",
      desc: "Spicy red and yellow peppers, farm-fresh.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Okra",
      desc: "Tender okra pods, perfect for okra soup.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=400&q=80",
      title: "Plantain",
      desc: "Ripe and unripe plantains for frying or boiling.",
    },
  ],
  [
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Stockfish",
      desc: "Premium dried stockfish for soups and stews.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "Garri",
      desc: "Finely processed cassava flakes, white and yellow.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "African Giant Snail",
      desc: "Delicacy, cleaned and ready to cook.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Ugu Leaves",
      desc: "Fresh fluted pumpkin leaves for soups.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      title: "Dry Fish",
      desc: "Locally smoked fish, rich in flavor.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "Groundnut",
      desc: "Crunchy roasted groundnuts, perfect snack.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Pepper Soup Spice",
      desc: "Aromatic blend for authentic pepper soup.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Ogbono Seeds",
      desc: "Wild mango seeds for thick, hearty soups.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Melon Seeds",
      desc: "For egusi and other traditional soups.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "White Beans",
      desc: "Soft, quick-cooking beans for moi-moi.",
    },
  ],
  [
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Yellow Garri",
      desc: "Popular in the South, rich in flavor.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=400&q=80",
      title: "Plantain Flour",
      desc: "Healthy swallow alternative.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Ogiri",
      desc: "Fermented condiment for soups and stews.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
      title: "Iru (Locust Beans)",
      desc: "Traditional flavor enhancer.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Okazi Leaves",
      desc: "Essential for Afang soup.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Afang Leaves",
      desc: "Nutritious, green, and fresh.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "Ponmo",
      desc: "Cow skin, a delicacy in many dishes.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1464306076886-debca5e8a6b0?auto=format&fit=crop&w=400&q=80",
      title: "Ground Egusi",
      desc: "Ready-to-cook melon seed powder.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80",
      title: "Vegetable Mix",
      desc: "Prepped for quick, healthy meals.",
    },
    {
      image:
        "https://images.unsplash.com/photo-1506089676908-3592f7389d4d?auto=format&fit=crop&w=400&q=80",
      title: "Brown Beans",
      desc: "Rich in protein, perfect for akara.",
    },
  ],
];

// CSS for smooth sliding animation
const sliderStyles = `
@keyframes slide-left {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes slide-right {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
}
.food-slider {
  display: flex;
  gap: 1.5rem;
  min-width: 600px;
  will-change: transform;
  animation-duration: 30s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
.food-slider-left { animation-name: slide-left; }
.food-slider-right { animation-name: slide-right; }
.food-slider:hover { animation-play-state: paused; }
`;

const FoodShowcase = () => (
  <section className="py-16 bg-white">
    <style>{sliderStyles}</style>
    <div className="container mx-auto px-4">
      <h2 className="text-2xl font-bold text-center mb-10 text-green-700">
        Explore Our Local Food Items
      </h2>
      <div className="space-y-8">
        {foodRows.map((row, i) => (
          <div key={i} className="overflow-x-auto scrollbar-hide">
            <div
              className={`food-slider ${i % 2 === 0 ? "food-slider-left" : "food-slider-right"
                }`}
              tabIndex={0}
              aria-label="Food items carousel"
            >
              {[...row, ...row].map((item, j) => (
                <div
                  key={j}
                  className="min-w-[220px] bg-green-50 rounded-lg shadow p-4 flex flex-col items-center"
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-28 h-28 object-cover rounded mb-3"
                  />
                  <div className="font-semibold text-green-700 mb-1">
                    {item.title}
                  </div>
                  <div className="text-gray-600 text-sm text-center">
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Order CTA Section
const OrderCTA = () => (
  <section className="py-16 bg-green-50">
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-2xl font-bold text-green-700 mb-4">
        Order Now – Fast, Easy, and Direct
      </h2>
      <p className="text-lg text-gray-700 mb-8">
        Ready to place an order? Reach us instantly on WhatsApp, fill out our
        contact form, or call our sales team.
      </p>
      <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
        <a
          href="https://wa.me/2348012345678"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold shadow hover:bg-green-700 transition"
        >
          Order via WhatsApp
        </a>
        <Link
          to="/contact"
          className="bg-white border border-green-600 text-green-700 px-6 py-3 rounded-lg font-semibold shadow hover:bg-green-50 transition"
        >
          Contact Form
        </Link>
        <a
          href="tel:+2348012345678"
          className="bg-green-100 text-green-700 px-6 py-3 rounded-lg font-semibold rounded shadow hover:bg-green-200 transition"
        >
          Call Sales: +234 801 234 5678
        </a>
      </div>
    </div>
  </section>
);

// Call to Action
const CTA = () => (
  <section className="py-16 bg-green-700">
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">
        Ready to join AgriFlow?
      </h2>
      <p className="text-lg text-green-100 mb-8">
        Sign up today and transform your food business with Nigeria’s leading
        food hub.
      </p>
      <Link
        to="/register"
        className="btn-primary bg-white text-green-700 px-8 py-4 rounded-lg font-semibold shadow"
      >
        Get Started
      </Link>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-8 bg-gray-900 text-gray-200">
    <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between">
      <div className="flex items-center space-x-3 mb-4 md:mb-0">
        <img
          src="/favicon.png"
          alt="AgriFlow logo"
          className="w-8 h-8 object-contain"
        />
        <span className="text-lg font-bold">AgriFlow Food Hub</span>
      </div>
      <div className="flex space-x-6">
        <Link to="/products" className="hover:underline">
          Products
        </Link>
        <Link to="/about" className="hover:underline">
          About
        </Link>
        <Link to="/contact" className="hover:underline">
          Contact
        </Link>
      </div>
      <div className="text-sm text-gray-400 mt-4 md:mt-0">
        © {new Date().getFullYear()} AgriFlow. All rights reserved.
      </div>
    </div>
  </footer>
);

const LandingPage: React.FC = () => {
  return (
    <main className="agri-theme-root flex-1 flex flex-col">
        {/* AWARENESS - Hook */}
        <Hero />
        <LiveAvailability />

        {/* INTEREST - Product Discovery */}
        <TodayDeals />
        <PopularProducts />
        <FoodShowcase />

        {/* DESIRE - Value Proposition */}
        <HowItWorks />
        <WhyChooseAgriFlow />
        <BulkCalculator />
        <WhoWeServe />

        {/* CONVICTION - Trust Building */}
        <CoverageAndImpact />
        <Testimonials />

        {/* ACTION - Convert */}
        <UnifiedCTA />
        <Footer />
    </main>
  );
};

export default LandingPage;
